import { findLast, forEachRight } from "lodash-es"
import { autorun, computed, observable, observe, reaction, runInAction } from "mobx"

import { Lifetime } from "@/lib/Lifetime.ts"
import { getLogger } from "@/lib/logging.ts"
import { RemoteLogModel } from "@/routes/tasks/TaskViewScene/RemoteLogModel.ts"
import { TaskPreviewPortModel } from "@/routes/tasks/TaskViewScene/TaskPreviewPortModel.ts"
import { TerminalModel } from "@/routes/tasks/TaskViewScene/TerminalModel.ts"

import type {
  AiAgentTask,
  AiAgentTaskHistoryItem,
  AvailableLog,
  PreviewProcessesApiState,
  RuntimeConfig,
  UPreviewProcessesApiState
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { Lifetimed } from "@/lib/Lifetime.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("TaskPreviewModel")

export class TaskPreviewModel implements Lifetimed {
  readonly previewProcessesState = observable.box<UPreviewProcessesApiState | null>(null)

  readonly listeningToPreviewProcessState = computed(() => this.previewProcessesState.get() != null)

  readonly isPortBeingListened = computed(() => {
    const previewState = this.previewProcessesState.get()
    return previewState?.className === "Running" && isPreviewPortBeingListened(previewState)
  })

  readonly portModel: TaskPreviewPortModel

  readonly previewApiStateWithHistory = computed(() => {
    const apiState = this.previewProcessesState.get()
    if (apiState == null || apiState.className !== "Running") {
      return apiState
    }

    let n: number | undefined = undefined
    if (apiState.commitHash == this.taskInitialCommitHash.get() && this.taskInitialCommitHash !== null) {
      n = 0
    }

    const historyItem = this.historyItems.get().find(item => {
      return item.commit.id === apiState.commitHash
    })

    if (historyItem !== undefined) {
      n = historyItem.number
    }

    return {
      ...apiState,
      n,
    }
  })

  readonly agentDeterminedRelativePath = computed(() => {
    const versions = this.historyItems.get()

    const currentVersion = findLast(versions, item => item.isActive)

    const currentVersionPath = currentVersion?.checkpoint?.previewUrlRelativePath
    if (currentVersionPath) {
      return currentVersionPath
    }

    forEachRight(versions, item => {
      const itemRelativePath = item.checkpoint?.previewUrlRelativePath
      if (itemRelativePath) {
        return itemRelativePath
      }
    })

    return undefined
  })

  readonly canSshToEnvironment = observable.box(false)

  readonly connectCommand = observable.box<string | null>(null)
  readonly tunnelCommand = observable.box<string | null>(null)
  readonly sshKeyCount = observable.box<number>(0)

  private readonly connectDropdownOpen = observable.box(false)
  private readonly tunnelDropdownOpen = observable.box(false)

  readonly onConnectButtonPress = (open: boolean) => {
    runInAction(() => this.connectDropdownOpen.set(open))
  }

  readonly onTunnelButtonPress = (open: boolean) => {
    runInAction(() => this.tunnelDropdownOpen.set(open))
  }

  readonly terminals = observable.map<string, TerminalModel>()

  readonly devToolsOpen = observable.box(false)

  readonly availableLogs = observable.box<AvailableLog[] | null>(null)

  constructor(
    readonly lifetime: Lifetime,
    readonly id: IObservableValue<string | null>,
    readonly idV2: IObservableValue<string | null>,
    readonly v2EnvId: IObservableValue<string | null>,
    readonly taskId: string,
    readonly historyItems: IObservableValue<AiAgentTaskHistoryItem[]>,
    readonly taskInitialCommitHash: IObservableValue<string | null>,
    private readonly queries: Queries,
    readonly task: IObservableValue<AiAgentTask | null>,
    readonly runtimeConfig: IObservableValue<RuntimeConfig | null>,
    readonly wakeupAutomaticallyStoppedPreview: boolean,
    readonly previewV2: boolean,
  ) {
    const runtimeConfigPort = computed(() => {
      const rc = runtimeConfig.get()
      if (!rc) return -1

      if (previewV2) {
        const previewState = this.previewProcessesState.get()
        return previewState?.className === "Running" ? (previewState.previewPortStatus?.first ?? -1) : -1
      }

      return rc.previewPort ?? -1
    })
    this.portModel = new TaskPreviewPortModel(
      queries,
      taskId,
      this.previewProcessesState,
      runtimeConfigPort,
      this.isPortBeingListened
    )
    this.lifetime.add(() => this.portModel.terminate(), "TaskPreviewModel:portModel")

    this.lifetime.add(observe(this.previewProcessesState, change => {
      // since `null` is an initial loading state, transition null -> UPreviewApiState only happens once during the initial load
      // `null` -> `Stopped` transition typically happens when the user opens the task after its preview was hibernated to save resources
      // other transitions from some UPreviewApiState to another UPreviewApiState may happen when we introduce an ability to manually stop the preview
      if (change.oldValue === null && change.newValue?.className === "Stopped") {
        if (wakeupAutomaticallyStoppedPreview) {
          log.info("Waking up automatically stopped preview")
          void this.restart()
        }
      }
    }), "TaskPreviewModel:previewApiState")

    let subscribeToPreviewLifetime: Lifetime | undefined

    this.lifetime.add(reaction(
      () => ({
        id: id.get(),
        idV2: idV2.get(),
        idV2EnvId: v2EnvId.get(),
      }),
      ({ id, idV2, idV2EnvId }) => {
        console.log(`TaskPreviewModel:subscribeToPreview (id=${id}, idV2=${idV2}, idV2EnvId=${idV2EnvId})`)

        subscribeToPreviewLifetime?.terminate()

        subscribeToPreviewLifetime = new Lifetime(this.lifetime, "TaskPreviewModel:subscribeToPreview")

        const taskPreview = this.queries.subscribeToPreviewProcess(id, idV2)
        subscribeToPreviewLifetime.add(taskPreview.subscribe(state => {
            runInAction(() => this.previewProcessesState.set(state))
          }
        ), "TaskPreviewModel:taskPreview")

        const logList = this.queries.subscribeToPreviewLogList(id, idV2)
        subscribeToPreviewLifetime.add(logList.subscribe(list =>
          runInAction(() => this.availableLogs.set(list))
        ), "TaskPreviewModel:logList")

        const canSshToPreview = this.queries.canSshToPreviewEnv(id, idV2EnvId)
        subscribeToPreviewLifetime.add(canSshToPreview.subscribe(value =>
          runInAction(() => this.canSshToEnvironment.set(value))
        ), "TaskPreviewModel:canSshToPreview")
      },
      {
        fireImmediately: true
      }
    ), "TaskPreviewModel:id")

    this.lifetime.add(autorun(() => {
      const previewId = this.id.get()
      const previewV2EnvId = this.v2EnvId.get()

      const availableLogs = this.availableLogs.get()
      if (availableLogs == null) {
        return
      }

      const logIds = new Set<string>()

      for (const log of availableLogs) {
        const logId = log.id
        logIds.add(logId)
        if (!this.terminals.has(logId)) {
          const lt = new Lifetime(this.lifetime, `TaskPreviewModel:logs:${logId}`)
          const remoteLog = new RemoteLogModel(
            logId,
            log.label,
            () => this.queries.subscribeToPreviewAppLogs(previewId, previewV2EnvId, logId),
            lt
          )
          const terminal = new TerminalModel(lt, null, remoteLog)
          runInAction(() => this.terminals.set(logId, terminal))
        }
      }

      for (const logId of this.terminals.keys()) {
        if (!logIds.has(logId)) {
          this.terminals.get(logId)?.lifetime?.terminate()
          runInAction(() => this.terminals.delete(logId))
        }
      }
    }), "TaskPreviewModel:logs")

    this.lifetime.add(autorun(async () => {
      const id = this.id.get()
      const v2EnvId = this.v2EnvId.get()
      if (this.connectDropdownOpen.get()) {
        const connectCommand = await this.getSshConnectCommand(id, v2EnvId)
        runInAction(() => this.connectCommand.set(connectCommand.command))
      }
    }), "TaskPreviewModel:connectCommand")

    this.lifetime.add(autorun(async () => {
      const id = this.id.get()
      if (this.tunnelDropdownOpen.get()) {
        const tunnelCommand = await this.getSshTunnelCommand(id)
        runInAction(() => this.tunnelCommand.set(tunnelCommand.command))
      }
    }), "TaskPreviewModel:tunnelCommand")
  }

  readonly restart = async () => {
    await this.queries.apiClient.restartPreview(this.taskId)
  }

  readonly recreate = async () => {
    await this.queries.apiClient.recreatePreview(this.taskId)
  }

  async getSshConnectCommand(id: string | null = this.id.get(), v2EnvId: string | null = this.v2EnvId.get()) {
    const keys = await this.queries.apiClient.putUserKeysIntoPreviewEnv(id, v2EnvId)
    const command = await this.queries.getSshConnectCommandForPreview(id, v2EnvId)

    return { keys, command }
  }

  async getSshTunnelCommand(id: string | null = this.id.get(), idV2: string | null = this.idV2.get(), v2EnvId: string | null = this.v2EnvId.get()) {
    const keys = await this.queries.apiClient.putUserKeysIntoPreviewEnv(id, v2EnvId)
    const command = await this.queries.getSshTunnelCommandForPreview(id, idV2)

    return { keys, command }
  }
}

export const allScriptsStarted = (state: UPreviewProcessesApiState | null) => {
  return state != null && state.className === "Running" &&
    state.warmupScriptState.className === "Finished" && state.warmupScriptState.exitCode == 0 &&
    state.processStates.every(processState =>
      processState.second.className === "Running" && processState.second.ready
    )
}

export function isPreviewPortBeingListened(state: PreviewProcessesApiState.Running) {
  const portStatus = state.previewPortStatus
  if (portStatus == null) {
    return false
  }

  return portStatus.second
}
